import { useMutation } from '@tanstack/react-query';
import { isEqual } from 'lodash';

import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import useEditableQuestion from '@/data/hook/question/useEditableQuestion';
import {
  createFlashcardQuestionIntoActivity,
  updateFlashcardQuestionIntoActivity,
} from '@/data/services/question/flashcardQuestionServices';
import Activity from '@/models/Activity';
import { FlashcardQuestion } from '@/models/Question';
import {
  adapterFeedbacks,
  sanitizeFeedbacks,
} from '@/utils/questions/adapterFeedbacks';
import { getQuestionErrorMessage } from '@/utils/questions/getQuestionErrorMessage';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import alert from '@/utils/shared/alert';
import FlashcardForm from './FlashcardForm';
import { FlashcardFormFields } from './FlashcardForm';

type FlashcardFormServiceProps = {
  activity: Activity;
  question: FlashcardQuestion;
  isActive?: boolean;
};

export default function FlashcardFormService({
  activity,
  question,
  isActive,
}: FlashcardFormServiceProps) {
  const gradeCtx = useQuestionExamGradeCtx();
  const { ensureEditable, resolveEditable } = useEditableQuestion(activity);

  const handleCreate = async (
    data: FlashcardFormFields,
  ): Promise<FlashcardQuestion> => {
    const { maxGrade, feedbacks, ...rest } = data;
    const editableActivity = await ensureEditable();
    const saved = await createFlashcardQuestionIntoActivity(
      editableActivity.id,
      {
        type: question.type,
        identifier: question.identifier,
        order: question.order,
        feedbacks: sanitizeFeedbacks(feedbacks),
        ...rest,
      },
    );
    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const handleUpdate = async (
    data: FlashcardFormFields,
  ): Promise<FlashcardQuestion> => {
    const { maxGrade, ...rest } = data;
    const oldValues: Omit<FlashcardFormFields, 'maxGrade'> = {
      title: normalizeTranslatableField(question.title),
      frontContent: normalizeTranslatableField(question.frontContent),
      backContent: normalizeTranslatableField(question.backContent),
      feedbacks: adapterFeedbacks(question.feedbacks ?? []),
      showFrontOnBack: question.showFrontOnBack ?? false,
    };

    const changedFields: Partial<Omit<FlashcardFormFields, 'maxGrade'>> = {};
    if (!isEqual(oldValues.title, rest.title)) {
      changedFields.title = rest.title;
    }

    if (!isEqual(oldValues.frontContent, rest.frontContent)) {
      changedFields.frontContent = rest.frontContent;
    }

    if (!isEqual(oldValues.showFrontOnBack, rest.showFrontOnBack)) {
      changedFields.showFrontOnBack = rest.showFrontOnBack;
    }

    if (!isEqual(oldValues.backContent, rest.backContent)) {
      changedFields.backContent = rest.backContent;
    }

    const newSanitizedFeedbacks = sanitizeFeedbacks(rest.feedbacks);
    const oldSanitizedFeedbacks = sanitizeFeedbacks(oldValues.feedbacks);
    if (!isEqual(oldSanitizedFeedbacks, newSanitizedFeedbacks)) {
      changedFields.feedbacks = newSanitizedFeedbacks;
    }

    const { updateTarget } = await resolveEditable(question);
    const saved = await updateTarget(
      changedFields,
      updateFlashcardQuestionIntoActivity,
    );

    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const { mutateAsync: submitQuestion, isPending } = useMutation({
    mutationFn: (data: FlashcardFormFields) =>
      !question.id ? handleCreate(data) : handleUpdate(data),
    onError: err => alert.error(getQuestionErrorMessage(err)),
  });

  return (
    <FlashcardForm
      key={question.identifier}
      isActive={isActive}
      question={question}
      onSubmit={submitQuestion}
      isLoading={isPending}
    />
  );
}
